/**
 * mSTEM Kit Detail Modal (SMH-04) — "Thông tin chi tiết kit SmartHome".
 *
 * Read-only presentational modal opened from the Kit Manager row action ℹ.
 * Three stacked panels:
 *   Panel A — device identity grid (mã kit, model, MCU, MAC, IP, sensor/relay…)
 *   Panel B — firmware summary: version đang cài vs version mới nhất + badge
 *             "Đã mới nhất" (success) / "Có bản mới" (warning)
 *   Panel C — firmware history table (MstemTable) with a "Đang dùng" badge on
 *             the row matching kit.firmwareVersion
 *
 * MOCK-ONLY: the kit metadata comes from the kits reducer (seeded by
 * src/lib/mstem-mock/smarthome-kits.js) and the version list from
 * src/lib/mstem-mock/firmwares.js — no device is ever queried. The container
 * passes `firmwares` so this component stays a dumb renderer.
 *
 * Empty-value policy (UI-SPEC Pitfall 4 — never-empty surface): every missing
 * string renders as EMPTY_VALUE ('—') instead of collapsing the row.
 *
 * NOC-navy header band + white body, mirroring SmartHomeDashboardModal.
 */
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';

import styles from './kit-detail-modal.css';

const EMPTY_VALUE = '—';

const messages = defineMessages({
    title: {
        defaultMessage: 'Thông tin chi tiết kit SmartHome',
        description: 'Kit detail modal title (SMH-04)',
        id: 'gen.kit.detail.title'
    },
    deviceSection: {
        defaultMessage: 'Thông tin thiết bị',
        description: 'Kit detail — device information panel head',
        id: 'gen.kit.detail.section.device'
    },
    firmwareSection: {
        defaultMessage: 'Firmware',
        description: 'Kit detail — firmware panel head',
        id: 'gen.kit.detail.section.firmware'
    },
    historySection: {
        defaultMessage: 'Lịch sử phiên bản firmware',
        description: 'Kit detail — firmware history panel head',
        id: 'gen.kit.detail.section.history'
    },
    labelId: {
        defaultMessage: 'Mã kit',
        description: 'Kit detail field — kit id',
        id: 'gen.kit.detail.label.id'
    },
    labelModel: {
        defaultMessage: 'Model',
        description: 'Kit detail field — model code',
        id: 'gen.kit.detail.label.model'
    },
    labelBrand: {
        defaultMessage: 'Hãng sản xuất',
        description: 'Kit detail field — brand',
        id: 'gen.kit.detail.label.brand'
    },
    labelMcu: {
        defaultMessage: 'Vi điều khiển',
        description: 'Kit detail field — MCU',
        id: 'gen.kit.detail.label.mcu'
    },
    labelHardware: {
        defaultMessage: 'Phiên bản phần cứng',
        description: 'Kit detail field — hardware revision',
        id: 'gen.kit.detail.label.hardware'
    },
    labelSerial: {
        defaultMessage: 'Số sê-ri',
        description: 'Kit detail field — serial number',
        id: 'gen.kit.detail.label.serial'
    },
    labelMac: {
        defaultMessage: 'Địa chỉ MAC',
        description: 'Kit detail field — MAC address',
        id: 'gen.kit.detail.label.mac'
    },
    labelIp: {
        defaultMessage: 'Địa chỉ IP',
        description: 'Kit detail field — IP address',
        id: 'gen.kit.detail.label.ip'
    },
    labelSensors: {
        defaultMessage: 'Số sensor',
        description: 'Kit detail field — sensor count',
        id: 'gen.kit.detail.label.sensors'
    },
    labelRelays: {
        defaultMessage: 'Số relay',
        description: 'Kit detail field — relay count',
        id: 'gen.kit.detail.label.relays'
    },
    labelRfid: {
        defaultMessage: 'Hỗ trợ RFID',
        description: 'Kit detail field — RFID support',
        id: 'gen.kit.detail.label.rfid'
    },
    labelActivated: {
        defaultMessage: 'Ngày kích hoạt',
        description: 'Kit detail field — activation date',
        id: 'gen.kit.detail.label.activated'
    },
    labelLastSync: {
        defaultMessage: 'Đồng bộ lần cuối',
        description: 'Kit detail field — last sync timestamp',
        id: 'gen.kit.detail.label.lastSync'
    },
    labelCurrentFw: {
        defaultMessage: 'Phiên bản đang cài',
        description: 'Kit detail firmware — installed version',
        id: 'gen.kit.detail.label.currentFw'
    },
    labelLatestFw: {
        defaultMessage: 'Phiên bản mới nhất',
        description: 'Kit detail firmware — latest available version',
        id: 'gen.kit.detail.label.latestFw'
    },
    labelReleaseDate: {
        defaultMessage: 'Ngày phát hành',
        description: 'Kit detail firmware — release date',
        id: 'gen.kit.detail.label.releaseDate'
    },
    labelSize: {
        defaultMessage: 'Dung lượng',
        description: 'Kit detail firmware — package size',
        id: 'gen.kit.detail.label.size'
    },
    labelNotes: {
        defaultMessage: 'Ghi chú phát hành',
        description: 'Kit detail firmware — release notes',
        id: 'gen.kit.detail.label.notes'
    },
    statusConnected: {
        defaultMessage: 'Đang kết nối',
        description: 'Kit connection status — connected',
        id: 'gen.kit.detail.status.connected'
    },
    statusOffline: {
        defaultMessage: 'Ngoại tuyến',
        description: 'Kit connection status — offline',
        id: 'gen.kit.detail.status.offline'
    },
    statusOutdated: {
        defaultMessage: 'Cần cập nhật',
        description: 'Kit connection status — firmware outdated',
        id: 'gen.kit.detail.status.outdated'
    },
    fwUpToDate: {
        defaultMessage: 'Đã là bản mới nhất',
        description: 'Firmware badge — already latest',
        id: 'gen.kit.detail.fw.upToDate'
    },
    fwOutdated: {
        defaultMessage: 'Có bản mới {version}',
        description: 'Firmware badge — newer version available',
        id: 'gen.kit.detail.fw.outdated'
    },
    fwInUse: {
        defaultMessage: 'Đang dùng',
        description: 'Firmware history row badge — currently installed',
        id: 'gen.kit.detail.fw.inUse'
    },
    rfidYes: {
        defaultMessage: 'Có',
        description: 'Kit detail — RFID supported',
        id: 'gen.kit.detail.rfid.yes'
    },
    rfidNo: {
        defaultMessage: 'Không',
        description: 'Kit detail — RFID not supported',
        id: 'gen.kit.detail.rfid.no'
    },
    colVersion: {
        defaultMessage: 'Phiên bản',
        description: 'Firmware history column — version',
        id: 'gen.kit.detail.col.version'
    },
    colReleaseDate: {
        defaultMessage: 'Ngày phát hành',
        description: 'Firmware history column — release date',
        id: 'gen.kit.detail.col.releaseDate'
    },
    colSize: {
        defaultMessage: 'Dung lượng',
        description: 'Firmware history column — size',
        id: 'gen.kit.detail.col.size'
    },
    colNotes: {
        defaultMessage: 'Ghi chú',
        description: 'Firmware history column — release notes',
        id: 'gen.kit.detail.col.notes'
    },
    historyEmpty: {
        defaultMessage: 'Chưa có dữ liệu firmware cho kit này',
        description: 'Firmware history empty state',
        id: 'gen.kit.detail.history.empty'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Kit detail close button',
        id: 'gen.kit.detail.close'
    }
});

// connectionStatus → {badge variant, label message}. Unknown/missing status
// falls back to "Ngoại tuyến" so the badge is never blank.
const STATUS_MAP = {
    connected: {variant: 'success', label: messages.statusConnected},
    offline: {variant: 'primary', label: messages.statusOffline},
    outdated: {variant: 'warning', label: messages.statusOutdated}
};

const displayValue = function (value) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) return value;
    return EMPTY_VALUE;
};

const KitDetailModalComponent = ({intl, kit, firmwares, onRequestClose}) => {
    const fwHistory = firmwares || [];
    // firmwares.js orders newest-first, so index 0 is the latest release.
    const latest = fwHistory.length ? fwHistory[0] : null;
    const current = fwHistory.find(f => f.version === kit.firmwareVersion) || null;
    const isUpToDate = Boolean(latest) && latest.version === kit.firmwareVersion;
    const status = STATUS_MAP[kit.connectionStatus] || STATUS_MAP.offline;

    const rows = [
        {key: 'id', label: messages.labelId, value: kit.id},
        {key: 'model', label: messages.labelModel, value: kit.model},
        {key: 'brand', label: messages.labelBrand, value: kit.brand},
        {key: 'mcu', label: messages.labelMcu, value: kit.mcu},
        {key: 'hardware', label: messages.labelHardware, value: kit.hardwareRevision},
        {key: 'serial', label: messages.labelSerial, value: kit.serialNumber},
        {key: 'mac', label: messages.labelMac, value: kit.macAddress},
        {key: 'ip', label: messages.labelIp, value: kit.ipAddress},
        {key: 'sensors', label: messages.labelSensors, value: kit.sensorCount},
        {key: 'relays', label: messages.labelRelays, value: kit.relayCount},
        {
            key: 'rfid',
            label: messages.labelRfid,
            value: intl.formatMessage(kit.hasRFID ? messages.rfidYes : messages.rfidNo)
        },
        {key: 'activated', label: messages.labelActivated, value: kit.activatedAt},
        {key: 'lastSync', label: messages.labelLastSync, value: kit.lastSyncAt}
    ];

    // Defined here (not inline in JSX) so the columns array stays free of
    // arrow props — matches the SmartHomeDashboardModal renderResult pattern.
    const renderVersionCell = row => (
        <span className={styles.versionCell}>
            <span className={styles.versionText}>{row.version}</span>
            {row.version === kit.firmwareVersion ? (
                <MstemBadge variant="success">
                    <FormattedMessage {...messages.fwInUse} />
                </MstemBadge>
            ) : null}
        </span>
    );
    const renderSizeCell = row => `${row.sizeKB} KB`;

    const columns = [
        {
            key: 'version',
            label: <FormattedMessage {...messages.colVersion} />,
            width: '180px',
            render: renderVersionCell
        },
        {
            key: 'releaseDate',
            label: <FormattedMessage {...messages.colReleaseDate} />,
            width: '140px'
        },
        {
            key: 'sizeKB',
            label: <FormattedMessage {...messages.colSize} />,
            width: '110px',
            render: renderSizeCell
        },
        {key: 'releaseNotes', label: <FormattedMessage {...messages.colNotes} />}
    ];

    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            headerClassName={styles.header}
            id="kitDetailModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                <div className={styles.titleRow}>
                    <span className={styles.kitName}>{kit.name}</span>
                    <MstemBadge variant={status.variant}>
                        <FormattedMessage {...status.label} />
                    </MstemBadge>
                </div>

                {/* Panel A — device identity */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.deviceSection} />
                    </p>
                    <dl className={styles.infoGrid}>
                        {rows.map(row => (
                            <div
                                key={row.key}
                                className={styles.infoItem}
                            >
                                <dt className={styles.infoLabel}>
                                    <FormattedMessage {...row.label} />
                                </dt>
                                <dd className={styles.infoValue}>
                                    {displayValue(row.value)}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Panel B — firmware summary */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.firmwareSection} />
                    </p>
                    <div className={styles.fwSummary}>
                        <div className={styles.fwVersionBlock}>
                            <span className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelCurrentFw} />
                            </span>
                            <span className={styles.fwVersion}>
                                {displayValue(kit.firmwareVersion)}
                            </span>
                        </div>
                        <div className={styles.fwVersionBlock}>
                            <span className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelLatestFw} />
                            </span>
                            <span className={styles.fwVersion}>
                                {latest ? latest.version : EMPTY_VALUE}
                            </span>
                        </div>
                        <div className={styles.fwBadgeBlock}>
                            {isUpToDate ? (
                                <MstemBadge variant="success">
                                    <FormattedMessage {...messages.fwUpToDate} />
                                </MstemBadge>
                            ) : (
                                <MstemBadge variant="warning">
                                    <FormattedMessage
                                        {...messages.fwOutdated}
                                        values={{version: latest ? latest.version : EMPTY_VALUE}}
                                    />
                                </MstemBadge>
                            )}
                        </div>
                    </div>
                    <dl className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                            <dt className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelReleaseDate} />
                            </dt>
                            <dd className={styles.infoValue}>
                                {current ? current.releaseDate : EMPTY_VALUE}
                            </dd>
                        </div>
                        <div className={styles.infoItem}>
                            <dt className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelSize} />
                            </dt>
                            <dd className={styles.infoValue}>
                                {current ? `${current.sizeKB} KB` : EMPTY_VALUE}
                            </dd>
                        </div>
                        <div className={styles.infoItemWide}>
                            <dt className={styles.infoLabel}>
                                <FormattedMessage {...messages.labelNotes} />
                            </dt>
                            <dd className={styles.infoValue}>
                                {current ? current.releaseNotes : EMPTY_VALUE}
                            </dd>
                        </div>
                    </dl>
                </div>

                {/* Panel C — firmware history */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.historySection} />
                    </p>
                    <MstemTable
                        columns={columns}
                        data={fwHistory}
                        emptyMessage={intl.formatMessage(messages.historyEmpty)}
                    />
                </div>

                <div className={styles.footer}>
                    <button
                        className={styles.closeButton}
                        type="button"
                        onClick={onRequestClose}
                    >
                        <FormattedMessage {...messages.close} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

KitDetailModalComponent.propTypes = {
    firmwares: PropTypes.arrayOf(PropTypes.shape({
        version: PropTypes.string,
        releaseDate: PropTypes.string,
        sizeKB: PropTypes.number,
        releaseNotes: PropTypes.string
    })),
    intl: intlShape.isRequired,
    kit: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        brand: PropTypes.string,
        sensorCount: PropTypes.number,
        relayCount: PropTypes.number,
        hasRFID: PropTypes.bool,
        model: PropTypes.string,
        mcu: PropTypes.string,
        hardwareRevision: PropTypes.string,
        serialNumber: PropTypes.string,
        macAddress: PropTypes.string,
        ipAddress: PropTypes.string,
        firmwareVersion: PropTypes.string,
        connectionStatus: PropTypes.string,
        activatedAt: PropTypes.string,
        lastSyncAt: PropTypes.string
    }).isRequired,
    onRequestClose: PropTypes.func.isRequired
};

KitDetailModalComponent.defaultProps = {
    firmwares: []
};

const KitDetailModal = injectIntl(KitDetailModalComponent);

export {
    KitDetailModal as default,
    KitDetailModalComponent,
    STATUS_MAP,
    EMPTY_VALUE
};
